/* eslint-disable react/prop-types */
import {
  AlertTriangle,
  CreditCard,
  Receipt,
  X,
  TimerOff,
  RotateCcw,
  Radio,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * PaxPaymentTimeoutDialog is displayed when a Pusher `order_payment_timeout` or `pre_order_advance_timeout` event is received.
 * It alerts the user that the PAX payment has timed out and provides options to dispatch a new payment
 * or cancel the popup.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose - Closes the dialog
 * @param {() => void} props.onNewPayment - Triggers a new terminal payment dispatch
 * @param {object} props.orderData - Current order data
 * @param {object} [props.timeoutData] - Incoming event payload
 * @param {boolean} [props.isLoading] - Loading state for new payment dispatch
 */
export default function PaxPaymentTimeoutDialog({
  isOpen,
  onClose,
  onNewPayment,
  orderData,
  timeoutData,
  isLoading = false,
  title,
  description,
  amountLabel,
  buttonText,
}) {
  const isRefund =
    timeoutData?.type?.includes("refund") ||
    timeoutData?.event_type?.includes("refund") ||
    orderData?.isRefund;

  const isPreOrderAdvance =
    timeoutData?.type === "pre_order_advance_timeout" ||
    timeoutData?.event_type === "pre_order_advance_timeout" ||
    timeoutData?.stage === "pre_order" ||
    orderData?.stage === "pre_order" ||
    Boolean(orderData?.pre_order);

  const isSplitOrCourseWise =
    timeoutData?.type?.includes("course_wise") ||
    timeoutData?.type?.includes("split") ||
    Boolean(timeoutData?.course_wise_payment) ||
    Boolean(timeoutData?.split) ||
    (timeoutData?.requested_amount !== undefined && timeoutData?.requested_amount !== null);

  const rawAmount =
    timeoutData?.requested_amount !== undefined && timeoutData?.requested_amount !== null
      ? timeoutData.requested_amount
      : timeoutData?.amount !== undefined && timeoutData?.amount !== null
      ? timeoutData.amount
      : timeoutData?.c2c_gift_card?.payer_to_pay !== undefined && timeoutData?.c2c_gift_card?.payer_to_pay !== null
      ? timeoutData.c2c_gift_card.payer_to_pay
      : timeoutData?.c2c_gift_card?.reward !== undefined && timeoutData?.c2c_gift_card?.reward !== null
      ? timeoutData.c2c_gift_card.reward
      : orderData?.advance !== undefined && orderData?.advance !== null
      ? orderData.advance
      : orderData?.payer_to_pay !== undefined && orderData?.payer_to_pay !== null
      ? orderData.payer_to_pay
      : orderData?.amount !== undefined && orderData?.amount !== null
      ? orderData.amount
      : orderData?.reward !== undefined && orderData?.reward !== null
      ? orderData.reward
      : timeoutData?.split?.remaining_amount !== undefined && timeoutData?.split?.remaining_amount !== null
      ? timeoutData.split.remaining_amount
      : timeoutData?.course_wise_payment?.remaining_amount !== undefined && timeoutData?.course_wise_payment?.remaining_amount !== null
      ? timeoutData.course_wise_payment.remaining_amount
      : orderData?.pre_order?.advance_amount !== undefined && orderData?.pre_order?.advance_amount !== null
      ? orderData.pre_order.advance_amount
      : orderData?.total_price !== undefined && orderData?.total_price !== null
      ? orderData.total_price
      : 0;

  const displayAmount = Number(parseFloat(rawAmount || 0)).toFixed(2);

  const deviceId =
    timeoutData?.device_id ||
    orderData?.device_id ||
    timeoutData?.shared_till_ids?.[0];

  const reference =
    orderData?.reference ||
    orderData?.unique_id ||
    (orderData?.code ? `VOUCHER-${orderData.code}` : "") ||
    timeoutData?.unique_id ||
    (timeoutData?.c2c_gift_card?.code ? `VOUCHER-${timeoutData.c2c_gift_card.code}` : "") ||
    (orderData?.id || timeoutData?.order_id ? `ORDER-${orderData?.id || timeoutData?.order_id}` : "REF");

  const resolvedAmountLabel =
    amountLabel ||
    (reference.startsWith("RES") || timeoutData?.event_type?.includes("reservation")
      ? "Deposit Amount"
      : reference.startsWith("VOUCHER") || timeoutData?.c2c_gift_card
      ? "Voucher Total"
      : isRefund
      ? "Refund Amount"
      : isPreOrderAdvance
      ? "Advance Amount"
      : isSplitOrCourseWise
      ? "Requested Amount"
      : "Order Total");

  const resolvedTitle =
    title ||
    (isRefund
      ? "PAX Refund Timed Out"
      : isPreOrderAdvance
      ? "PAX Advance Payment Timed Out"
      : "PAX Payment Timed Out");

  const resolvedDescription =
    description ||
    (isRefund
      ? "The refund request on the PAX terminal has timed out. You can dispatch a fresh refund request to proceed."
      : isPreOrderAdvance
      ? "The advance payment request on the PAX terminal has timed out. You can dispatch a new payment request to proceed."
      : "The payment request on the PAX terminal has timed out. You need to dispatch a new payment request to proceed.");

  const resolvedButtonText =
    buttonText ||
    (isRefund
      ? reference?.startsWith("RES") || timeoutData?.event_type?.includes("reservation")
        ? "Cancel Again"
        : "New Refund"
      : isPreOrderAdvance
      ? "Retry Advance Payment"
      : "New Payment");

  const isCancelAction = isRefund && resolvedButtonText.toLowerCase().includes("cancel");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[460px] bg-cblack4 border-gray-800 text-white overflow-hidden p-6 animate-fadeIn shadow-2xl">
        <DialogHeader className="flex flex-col justify-center items-center gap-2 text-center">
          {/* Animated Warning Icon Badge */}
          <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/30">
            <TimerOff className="h-7 w-7 z-10" />
            <span className="absolute inset-0 rounded-full bg-rose-500/20 animate-ping opacity-60" />
          </div>

          <DialogTitle className="text-white text-xl font-bold tracking-tight">
            {resolvedTitle}
          </DialogTitle>
          <DialogDescription className="text-cwhite3 text-xs max-w-[360px]">
            {resolvedDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-3">
          {/* Prominent Amount & Reference Card */}
          <div className="bg-cblack3 p-4 rounded-xl border border-gray-800 flex items-center justify-between shadow-inner">
            <div className="flex flex-col">
              <span className="text-[10px] text-cwhite3 uppercase tracking-wider font-semibold">
                {resolvedAmountLabel}
              </span>
              <span className="text-cyellow text-2xl font-black font-sans mt-0.5">
                £{displayAmount}
              </span>
            </div>

            <div className="text-right flex flex-col gap-1 items-end">
              <span className="text-xs font-semibold text-cwhite2 flex items-center gap-1">
                <Receipt className="h-3 w-3 text-cyan-400" />
                {reference}
              </span>
              {deviceId && (
                <span className="text-[11px] text-cwhite3 flex items-center justify-end gap-1">
                  <Radio className="h-3 w-3 text-amber-400" />
                  Terminal #{deviceId}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500/15 text-rose-400 border border-rose-500/30">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                {isRefund ? "Refund Timed Out" : "Payment Timed Out"}
              </span>
            </div>
          </div>

          {/* Explanation Alert Box */}
          <div className="p-3.5 rounded-lg border border-amber-500/25 bg-amber-500/5 text-xs text-cwhite3 flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <span>
              {isRefund
                ? <>The terminal did not complete the refund in time. Click <strong>&quot;{resolvedButtonText}&quot;</strong> to retry cancelling and dispatching the deposit refund, or <strong>&quot;Cancel&quot;</strong> to abort.</>
                : <>The customer did not complete the transaction in time on the terminal. Click <strong>&quot;{resolvedButtonText}&quot;</strong> to dispatch a fresh request to the PAX device, or <strong>&quot;Cancel&quot;</strong> to choose another payment method.</>
              }
            </span>
          </div>
        </div>

        <DialogFooter className="mt-4 flex flex-col sm:flex-col gap-2.5">
          <Button
            type="button"
            disabled={isLoading}
            className={`w-full ${isCancelAction ? "bg-cred hover:bg-cred/90 text-white shadow-red-500/10" : "bg-cyan-500 hover:bg-cyan-600 text-white shadow-cyan-500/10"} font-bold py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-lg text-sm`}
            onClick={onNewPayment}
          >
            {isCancelAction ? <RotateCcw className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
            {resolvedButtonText}
          </Button>

          <Button
            type="button"
            variant="outline"
            disabled={isLoading}
            className="w-full bg-cblack3 border-gray-700 hover:bg-gray-800 text-cwhite2 font-semibold py-2.5 rounded-lg transition flex items-center justify-center gap-2 text-sm"
            onClick={onClose}
          >
            <X className="h-4 w-4 text-cwhite3" />
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
